import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, MapPin } from "lucide-react";
import { Logo } from "@/components/Logo";
import { useSession } from "@/context/SessionContext";
import { useLang } from "@/context/LanguageContext";
import { restaurant } from "@/data/restaurant";
import { isApiMenuSource } from "@/config/menuSource";

export default function EntryPage() {
  const navigate = useNavigate();
  const { tableSlug } = useParams();
  const { setTableFromSlug } = useSession();
  const { lang, t } = useLang();
  const [table, setTable] = useState(null);

  useEffect(() => {
    const resolved = setTableFromSlug(tableSlug || "demo-table-12");
    setTable(resolved);
    const timer = setTimeout(() => navigate("/home", { replace: true }), 2100);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableSlug]);

  return (
    <div className="relative mx-auto flex min-h-screen w-full max-w-md flex-col items-center justify-center overflow-hidden bg-background px-8" data-testid="entry-page">
      {/* ambient glow */}
      <div className="pointer-events-none absolute left-1/2 top-1/3 h-72 w-72 -translate-x-1/2 rounded-full bg-primary/20 blur-3xl" />

      <motion.div
        initial={{ scale: 0.6, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ type: "spring", damping: 14, stiffness: 180 }}
        className="relative z-10"
      >
        <Logo size="lg" />
      </motion.div>

      <motion.p
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="z-10 mt-3 text-sm text-muted-foreground"
      >
        {restaurant.name} · {restaurant.branch.name}
      </motion.p>

      {/* Table confirmation (no table model behind the real menu yet: never invent one) */}
      {(!isApiMenuSource() || table?.tableNumber != null) && (
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6, type: "spring", stiffness: 200 }}
        className="z-10 mt-10 flex items-center gap-3 rounded-full border border-border bg-card px-6 py-3.5 shadow-float"
        data-testid="table-confirmation"
      >
        <motion.span
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.85, type: "spring", stiffness: 300 }}
          className="grid h-8 w-8 place-items-center rounded-full bg-success text-white"
        >
          <Check className="h-5 w-5" strokeWidth={3} />
        </motion.span>
        <div className="text-start">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            {t("scanConfirm")}
          </p>
          <p className="font-heading text-xl font-extrabold leading-none">
            {t("table")} {table?.tableNumber ?? "12"}
          </p>
        </div>
      </motion.div>
      )}

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.1 }}
        className="z-10 mt-8 flex items-center gap-1.5 text-xs text-muted-foreground"
      >
        <MapPin className="h-3.5 w-3.5" />
        {[restaurant.branch.name, restaurant.branch.city].filter(Boolean).join(", ")}
      </motion.div>

      {/* loading bar */}
      <motion.div className="absolute bottom-16 h-1 w-40 overflow-hidden rounded-full bg-secondary">
        <motion.div
          className="h-full brand-gradient"
          initial={{ width: "0%" }}
          animate={{ width: "100%" }}
          transition={{ duration: 2, ease: "easeInOut" }}
        />
      </motion.div>
    </div>
  );
}
